import React from "react";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, ArrowLeft, ArrowRight, Tag, Phone } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

const categoryColors = {
  "Water Treatment": "bg-cyan-500/10 text-cyan-300 border-cyan-400/20",
  "Arsenic Removal": "bg-red-500/10 text-red-300 border-red-400/20",
  "Water Purification": "bg-blue-500/10 text-blue-300 border-blue-400/20",
  "Iron Removal": "bg-orange-500/10 text-orange-300 border-orange-400/20",
  "Softener Plant": "bg-emerald-500/10 text-emerald-300 border-emerald-400/20",
  "Water Filter": "bg-violet-500/10 text-violet-300 border-violet-400/20",
};

function ContentBlock({ block }) {
  if (block.type === "heading") {
    return (
      <h2 className="mt-10 mb-4 text-2xl font-bold text-white">{block.text}</h2>
    );
  }
  if (block.type === "paragraph") {
    return (
      <p className="mb-5 leading-8 text-slate-300">{block.text}</p>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="mb-5 space-y-2.5">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 leading-7 text-slate-300">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prev = blogPosts[currentIndex - 1] ?? null;
  const next = blogPosts[currentIndex + 1] ?? null;
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="overflow-x-hidden">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[320px] w-full overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20" />

        <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-4xl px-6 pb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/blog"
              className="mb-5 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 transition"
            >
              <ArrowLeft size={15} /> Back to Blog
            </Link>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${categoryColors[post.category] ?? "bg-cyan-500/10 text-cyan-300 border-cyan-400/20"}`}>
                <Tag size={11} />
                {post.category}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <Calendar size={13} />
                {post.date}
              </span>
              <span className="text-xs text-slate-400">By {post.author}</span>
            </div>

            <h1 className="text-3xl font-bold leading-tight md:text-5xl">
              {post.title}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-6 pb-20 pt-12">
        <div className="grid gap-10 lg:grid-cols-[1fr_280px]">
          {/* Article body */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="mb-6 text-lg font-medium leading-8 text-slate-200">
              {post.excerpt}
            </p>
            <hr className="mb-8 border-white/10" />

            {post.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}

            {/* Prev / Next */}
            <div className="mt-14 grid gap-4 sm:grid-cols-2">
              {prev && (
                <Link
                  to={`/blog/${prev.slug}`}
                  className="group flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:border-cyan-400/30"
                >
                  <span className="flex items-center gap-2 text-xs text-slate-500">
                    <ArrowLeft size={13} /> Previous
                  </span>
                  <span className="text-sm font-semibold leading-snug group-hover:text-cyan-300 transition">
                    {prev.title}
                  </span>
                </Link>
              )}
              {next && (
                <Link
                  to={`/blog/${next.slug}`}
                  className="group flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/5 p-5 text-right transition hover:border-cyan-400/30 sm:ml-auto sm:col-start-2"
                >
                  <span className="flex items-center justify-end gap-2 text-xs text-slate-500">
                    Next <ArrowRight size={13} />
                  </span>
                  <span className="text-sm font-semibold leading-snug group-hover:text-cyan-300 transition">
                    {next.title}
                  </span>
                </Link>
              )}
            </div>
          </motion.article>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* CTA card */}
            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/5 p-6">
              <h3 className="font-bold text-white">Need a Free Water Test?</h3>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                We test your water and design the right treatment plant for
                your property in Kolkata, Newtown or Rajarhat.
              </p>
              <a
                href="tel:+918697702088"
                className="mt-4 flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-cyan-400"
              >
                <Phone size={15} /> Call 8697702088
              </a>
              <Link
                to="/contact"
                className="mt-2 flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold transition hover:bg-white/10"
              >
                Get Free Proposal
              </Link>
            </div>

            {/* Services list */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-4 font-bold text-white">Our Services</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                {[
                  "Arsenic Removal Plant",
                  "Iron Removal Plant",
                  "RO Plant Installation",
                  "UV System",
                  "Softener Plant",
                  "Water Filter Installation",
                  "Water Treatment Plant",
                  "Filtration Plant",
                ].map((s) => (
                  <li key={s} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                    {s}
                  </li>
                ))}
              </ul>
              <Link
                to="/solutions"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition"
              >
                View All Solutions <ArrowRight size={14} />
              </Link>
            </div>

            {/* Areas served */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-3 font-bold text-white">Areas Served</h3>
              <div className="flex flex-wrap gap-2">
                {["Newtown", "Rajarhat", "Salt Lake", "Kolkata North", "Kolkata South", "Greater Kolkata"].map((a) => (
                  <span key={a} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-400">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Related posts */}
        <div className="mt-16">
          <h2 className="mb-8 text-2xl font-bold">Related Articles</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((rp, i) => (
              <motion.div
                key={rp.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                <Link
                  to={`/blog/${rp.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition hover:border-cyan-400/30"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={rp.image}
                      alt={rp.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className={`mb-2 inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${categoryColors[rp.category] ?? "bg-cyan-500/10 text-cyan-300 border-cyan-400/20"}`}>
                      {rp.category}
                    </span>
                    <h3 className="flex-1 text-sm font-semibold leading-snug group-hover:text-cyan-300 transition">
                      {rp.title}
                    </h3>
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                      <Calendar size={12} /> {rp.date}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
